/** Onboarding wizard copy (D20). */

/** First token of display name for “Welcome, {name}”. */
export function onboardingWelcomeName(
  firstName?: string | null,
  email?: string | null,
): string {
  const trimmed = firstName?.trim();
  if (trimmed) {
    return trimmed.split(/\s+/)[0] ?? trimmed;
  }
  const local = email?.split("@")[0]?.trim();
  if (local) {
    return local.charAt(0).toUpperCase() + local.slice(1);
  }
  return "there";
}

/** “Let's connect {company}'s data” line on the welcome screen. */
export function onboardingCompanyDataLine(companyName?: string | null): string {
  const name = companyName?.trim();
  if (name) {
    const possessive = name.endsWith("s") ? `${name}'` : `${name}'s`;
    return `Let's connect ${possessive} data`;
  }
  return "Let's connect your company's data";
}

export const ONBOARDING_COPY = {
  WELCOME_GREETING: (name: string) => `Welcome, ${name}`,
  WELCOME_MEET_PREFIX: "Meet",
  WELCOME_PRODUCT_NAME: "Saras iQ",
  WELCOME_TAGLINE: "Your e-commerce AI data analyst",
  WELCOME_INSIGHTS_SUFFIX: "and turn your questions into insights.",
  WELCOME_GET_STARTED: "Get started",
  WELCOME_SKIP: "Skip for now",
  WELCOME_LATER_HINT:
    "You can complete onboarding later from your main screen.",

  SETUP_ALREADY_COMPLETE_TITLE: "Onboarding is complete",
  SETUP_ALREADY_COMPLETE_BODY:
    "Your workspace setup is done. To manage your warehouse or data sources, open Integrations in Settings.",
  SETUP_GO_TO_INTEGRATIONS: "Go to Integrations",
  SETUP_GO_TO_CHAT: "Go to chat",

  GET_STARTED_TITLE: "Get started with iQ",
  GET_STARTED_BODY:
    "Connect your BigQuery warehouse in Daton, then add data sources in Pulse or Daton. You can finish setup in a few minutes.",
  START_SETUP: "Start setup",

  SETUP_DATA_TITLE: "Set up your data",
  SETUP_DATA_SUBTITLE:
    "Connect your BigQuery warehouse, then add the data sources you need.",
  /** Section kicker on `/setup/data` — e.g. ``STEP 1 · WAREHOUSE``. */
  SETUP_SECTION_LABEL: (step: 1 | 2, section: "WAREHOUSE" | "SOURCES") =>
    `STEP ${step} · ${section}`,
  /** @deprecated Use {@link ONBOARDING_COPY.SETUP_SECTION_LABEL} */
  SECTION_WAREHOUSE: "WAREHOUSE",
  /** @deprecated Use {@link ONBOARDING_COPY.SETUP_SECTION_LABEL} */
  SECTION_SOURCES: "SOURCES",
  BQ_CARD_TITLE: "Google BigQuery",
  BQ_STATUS_NOT_CONNECTED: "Not connected",
  BQ_CARD_SUBTITLE: "Manage where your data is stored",
  BQ_CONNECT_BTN: "Connect BigQuery",
  BQ_MANAGE_BTN: "Manage BigQuery",
  BQ_MANAGE_WAREHOUSE_BTN: "Manage warehouse",
  BQ_OPENS_IN_DATON: "(opens in Daton)",
  BQ_CONNECTED_BADGE: "Connected",
  STEP1_REGION: "Region",
  BACK_TO_WELCOME: "Back",

  STEP1_TITLE: "Connect your warehouse",
  STEP1_BODY:
    "Open Daton in a new tab to connect BigQuery. When you return, we refresh your connection status here.",
  STEP1_OPEN_DATON: "Open Daton",
  STEP1_CONNECTED: "Warehouse connected",
  STEP1_PROJECT: "Project",
  STEP1_DATASET: "Dataset",
  BQ_STATUS_CONFLICT:
    "Multiple BigQuery destinations found. Pick or consolidate one in Daton.",
  WAREHOUSE_CANDIDATES_LIST_ARIA: "BigQuery destinations in Daton",
  WAREHOUSE_CANDIDATES_TABLE_PAGINATION_ARIA: "BigQuery destinations table pages",
  WAREHOUSE_CANDIDATES_SEARCH: "Search warehouses…",
  WAREHOUSE_CANDIDATES_SEARCH_LABEL: "Search warehouses",
  WAREHOUSE_CANDIDATES_SUMMARY: (destinations: number) =>
    `${destinations} ${destinations === 1 ? "destination" : "destinations"}`,
  WAREHOUSE_CANDIDATE_ID_LABEL: "Warehouse ID",
  WAREHOUSE_CANDIDATE_ID: (id: number | string) => `Warehouse ID ${id}`,
  WAREHOUSE_CANDIDATE_DATAWAREHOUSE_ID: (id: number | string) =>
    `Daton template ${id}`,
  WAREHOUSE_CANDIDATE_SOURCES: (n: number) =>
    `${n} connected source${n === 1 ? "" : "s"}`,
  WAREHOUSE_CANDIDATE_NEW_PLATFORM: "New platform destination",
  WAREHOUSE_CANDIDATE_STATUS: "Status",
  WAREHOUSE_ROW_ACTIONS_ARIA: (name: string) => `Actions for ${name}`,
  WAREHOUSE_EDIT_DESTINATION: "Edit destination",
  WAREHOUSE_DELETE: "Delete warehouse",
  WAREHOUSE_DELETE_TITLE: "Delete warehouse",
  WAREHOUSE_DELETE_BODY:
    "All your current integrations will be disabled and cannot be recovered. Do you wish to continue?",
  WAREHOUSE_DELETE_CANCEL: "No",
  WAREHOUSE_DELETE_CONFIRM: "Yes, delete",
  WAREHOUSE_DELETED_TOAST: "Warehouse deleted",
  WAREHOUSE_STATUS_DELETED: "Deleted",
  WAREHOUSE_STATUS_ENABLED: "Enabled",
  WAREHOUSE_CANDIDATE_TABLE_SOURCES: "Sources",
  WAREHOUSE_CANDIDATE_TABLE_TEMPLATE: "Template",
  STEP1_CONTINUE: "Continue to sources",

  STEP2_TITLE: "Connect your data sources",
  STEP2_BODY:
    "Connect each source below. We'll open Pulse or Daton in a new tab for you.",
  STEP2_KICKOFF_HEADING: "Discussed on your kickoff call",
  STEP2_CONNECT: "Connect",
  STEP2_SOURCE_ROW_HINT: (sourceName: string) =>
    `Connect your ${sourceName} account`,
  STEP2_CONNECTED: "Connected",
  STEP2_MANAGE: "Manage",
  STEP2_SOURCES_LIST_ARIA: "Available connectors",
  STEP2_ADD_OTHER_SOURCES: (n: number) => `Add other sources (${n})`,
  STEP2_SHOW_FEWER_SOURCES: "Show fewer sources",
  STEP2_PULSE_CARD: "Pulse",
  STEP2_PULSE_DESC: "Marketing and commerce sources configured in Pulse.",
  STEP2_PULSE_DESC_SHORT: "Commerce and marketing",
  STEP2_DATON_CARD: "Daton",
  STEP2_DATON_DESC: "Ingest connectors managed in Daton.",
  STEP2_DATON_DESC_SHORT: "Marketplaces, ads and operations",
  STEP2_OPEN_HUB: "Open hub",
  STEP2_OPEN_PULSE: "Open Pulse",
  STEP2_OPEN_DATON: "Open Daton",
  STEP2_VIEW_SOURCES: (n: number) => `View sources (${n})`,
  STEP2_CHECKBOX_ATTESTATION: "I have connected everything I need to start.",
  STEP2_CONTINUE: "Continue",
  STEP2_CONTINUE_FOOTNOTE:
    "Connect your BigQuery warehouse and at least one source in Pulse or Daton to continue.",
  STEP2_CONTINUE_HINT_WAREHOUSE: "Connect your warehouse in Daton to enable Continue.",
  STEP2_CONTINUE_HINT_WAREHOUSE_CONFLICT:
    "Pick or consolidate one BigQuery destination in Daton to enable Continue. You can connect sources below.",
  STEP2_CONTINUE_HINT_SOURCES:
    "Connect at least one source in Pulse or Daton to enable Continue.",
  STEP2_RPA_FOOTER:
    "Need a custom or RPA connector? Contact Saras onboarding — we do not list those here.",

  STEP3_TITLE: "What happens next",
  STEP3_BODY:
    "Our team reviews your warehouse and sources. You can use iQ chat once your company is marked ready (your admin will be notified).",
  STEP3_FINISH: "Finish setup",

  SETUP_STATUS_TITLE: "Saras iQ setup",
  SETUP_STATUS_BODY:
    "You've completed the setup steps. Our team will take it from here. We'll notify you via Slack or email once Saras iQ is ready to use.",
  SETUP_STATUS_NEXT_LABEL: "What happens next",
  SETUP_STATUS_STEP_SYNC_TITLE: "Data sync",
  SETUP_STATUS_STEP_SYNC_BODY:
    "We'll help bring your source data into BigQuery.",
  SETUP_STATUS_STEP_VALIDATION_TITLE: "Validation",
  SETUP_STATUS_STEP_VALIDATION_BODY:
    "Our team will review the data and check that it's ready for analysis.",
  SETUP_STATUS_STEP_CONTEXT_TITLE: "Context layer setup",
  SETUP_STATUS_STEP_CONTEXT_BODY:
    "We'll prepare the business definitions that help Saras iQ understand your questions.",
  SETUP_STATUS_REVIEW_SETUP: "Review setup",
  SETUP_STATUS_COMPOSER_PLACEHOLDER: "Ask Saras iQ about your business…",
  SETUP_STATUS_COMPOSER_FOOTNOTE:
    "Chat will be available once Saras iQ is ready.",

  INTEGRATIONS_TAB_APPS: "Apps",
  INTEGRATIONS_TAB_SOURCES: "Sources",
  INTEGRATIONS_TAB_WAREHOUSE: "Warehouse",
  INTEGRATIONS_INVITE_BANNER_TITLE: "Don't have credentials?",
  INTEGRATIONS_INVITE_BANNER_BODY:
    "Invite a colleague who does. They join as an admin.",
  INTEGRATIONS_INVITE_TEAMMATE: "Invite a teammate",
  INTEGRATIONS_CONNECTED_SOURCES_TITLE: "Connected sources",
  // Counts source types: the BFF returns one connected row per type, so two Shopify
  // stores are one row and "connections" would undercount.
  INTEGRATIONS_CONNECTED_SUMMARY: (sources: number) =>
    `${sources} ${sources === 1 ? "source" : "sources"}`,
  INTEGRATIONS_SEARCH_CONNECTIONS: "Search connections…",
  INTEGRATIONS_SEARCH_CONNECTIONS_LABEL: "Search connections",
  INTEGRATIONS_ADD_SOURCE: "Add source",
  INTEGRATIONS_ADD_SOURCE_DIALOG_TITLE: "Add a source",
  INTEGRATIONS_ADD_SOURCE_DIALOG_SUBTITLE:
    "Choose a source to connect. Each source can have more than one account.",
  INTEGRATIONS_ADD_SOURCE_SEARCH: "Search sources…",
  INTEGRATIONS_ADD_SOURCE_SEARCH_LABEL: "Search sources",
  INTEGRATIONS_ADD_SOURCE_CATEGORY_ALL: "All",
  INTEGRATIONS_ADD_SOURCE_STATUS_CONNECTED: "Connected",
  INTEGRATIONS_ADD_SOURCE_STATUS_NOT_CONNECTED: "Not connected",
  INTEGRATIONS_ADD_SOURCE_DONE: "Done",
  INTEGRATIONS_ADD_SOURCE_EMPTY: "No sources match your search.",
  INTEGRATIONS_TABLE_NAME: "Name",
  INTEGRATIONS_TABLE_SOURCE_TYPE: "Source type",
  INTEGRATIONS_TABLE_DESTINATION: "Destination",
  INTEGRATIONS_TABLE_STATUS: "Status",
  INTEGRATIONS_TABLE_LAST_SYNCED: "Last synced",
  INTEGRATIONS_STATUS_CONNECTED: "Connected",
  INTEGRATIONS_STATUS_PAUSED: "Paused",
  INTEGRATIONS_STATUS_SCHEDULED: "Scheduled",
  INTEGRATIONS_STATUS_CONFIGURING: "Configuring",
  INTEGRATIONS_STATUS_INACTIVE: "Inactive",
  INTEGRATIONS_ACTION_REAUTHENTICATE: "Re-authenticate in Daton",
  INTEGRATIONS_ACTION_EDIT: "Edit configuration",
  INTEGRATIONS_ACTION_DETAILS: "Integration details",
  INTEGRATIONS_ACTION_PAUSE: "Pause in Daton",
  INTEGRATIONS_ACTION_DELETE: "Delete in Daton",
  INTEGRATIONS_ACTION_SYNC: "Sync in Daton",
  INTEGRATIONS_ACTION_ADD_ACCOUNT: "Add another account",
  INTEGRATIONS_PRODUCT_PULSE: "Pulse",
  INTEGRATIONS_PRODUCT_DATON: "Daton",
  INTEGRATIONS_SOURCES_SECTION: "Sources",
  INTEGRATIONS_SOURCES_SECTION_DESC: "Manage your source accounts.",
  INTEGRATIONS_WAREHOUSE_SECTION: "Warehouse",
  INTEGRATIONS_WAREHOUSE_SECTION_DESC:
    "Manage where your source data is stored.",
  INTEGRATIONS_PULSE_DESC:
    "Connect your commerce and marketing data sources through Pulse.",
  INTEGRATIONS_DATON_DESC:
    "Connect additional marketplaces, advertising, and business data through Daton.",
  INTEGRATIONS_MANAGE_SOURCES: "Manage sources",
  INTEGRATIONS_MANAGE_BQ: "Manage BigQuery",
  INTEGRATIONS_CONNECTED_COUNT: (n: number) =>
    n === 1 ? "1 connected" : `${n} connected`,
  INTEGRATIONS_LAST_SYNCED_EMPTY: "—",
  INTEGRATIONS_LAST_SYNCED_SCHEDULED: "Scheduled",
  NO_CONNECTORS_LISTED: "No connectors listed yet.",
  INTEGRATIONS_NO_CONNECTED_SOURCES: "No connected sources yet.",
  INTEGRATIONS_SOURCES_LOAD_ERROR:
    "Could not load connected sources. Check your connection and try again.",
  INTEGRATIONS_WAREHOUSE_LOAD_ERROR:
    "Could not load your warehouse status. Check your connection and try again.",
  INTEGRATIONS_CATALOG_UNAVAILABLE:
    "Could not reach Pulse or Daton to list sources. Try again in a moment.",
  INTEGRATIONS_SETTINGS_EYEBROW: "Settings",
  INTEGRATIONS_TABLE_ACTIONS: "Actions",
  INTEGRATIONS_TABLE_PAGE_PREVIOUS: "Previous",
  INTEGRATIONS_TABLE_PAGE_NEXT: "Next",
  INTEGRATIONS_TABLE_PAGINATION_ARIA: "Connected sources table pages",
  INTEGRATIONS_TABLE_SHOWING: (start: number, end: number, total: number) =>
    `Showing ${start}–${end} of ${total}`,
  INTEGRATIONS_ROW_ACTIONS_ARIA: (name: string) => `Actions for ${name}`,
  INTEGRATIONS_CELL_EXPAND: (column: string) => `Show full ${column}`,
  INTEGRATIONS_CELL_COLLAPSE: (column: string) => `Collapse ${column}`,
  INTEGRATIONS_TABLE_SORT_ASC: (column: string) => `Sort ${column} ascending`,
  INTEGRATIONS_TABLE_SORT_DESC: (column: string) => `Sort ${column} descending`,
  INTEGRATIONS_TABLE_SORT_BY: (column: string) => `Sort by ${column}`,
  INTEGRATIONS_TABLE_SORT_ASC_ACTIVE: (column: string) =>
    `${column} sorted ascending. Click to sort descending.`,
  INTEGRATIONS_TABLE_SORT_DESC_ACTIVE: (column: string) =>
    `${column} sorted descending. Click to clear sorting.`,
  INTEGRATIONS_ADD_SOURCE_ARIA: (name: string) => `Add ${name}`,
  INTEGRATIONS_CATEGORY_FILTER_ARIA: "Filter sources by category",
  RETRY: "Try again",
  INTEGRATIONS_BQ_SUBTITLE: (project: string, dataset: string) =>
    `${project} · ${dataset}`,

  LOADING: "Loading setup status…",
  LOADING_WAREHOUSE: "Loading warehouse…",
  LOADING_SOURCES: "Loading sources…",
  REFETCH_ON_FOCUS: "Checking warehouse status…",
  REFETCH_SOURCES_ON_FOCUS: "Refreshing connected sources…",
  POPUP_BLOCKED: "Allow pop-ups for this site to open Daton or Pulse.",
  SETUP_UNAVAILABLE_TITLE: "Setup unavailable",
  SETUP_LOAD_FAILED:
    "Could not load your setup status. Refresh the page to try again.",
  SETUP_SOURCES_LOAD_FAILED:
    "Could not load the source list. Refresh the page to try again.",
  CONTINUE_FAILED:
    "Could not save your setup. Wait a moment, then select Continue again.",
  WAREHOUSE_LOAD_FAILED:
    "Could not load your warehouse status. Refresh the page to try again.",
  WAREHOUSE_STATUS_UNREADABLE:
    "Could not read warehouse status from Daton. Try again in a moment.",
  SESSION_EXPIRED: "Your session expired. Refresh the page or sign in again.",
  BQ_STATUS_FORBIDDEN:
    "We could not read warehouse status from Daton yet. Open Daton to connect BigQuery, then return here.",
  SETUP_COMPANY_FALLBACK: "Your company",
  CONTINUE_BLOCKED: "Connect your warehouse in Daton before continuing.",
  CONTINUE_BLOCKED_SOURCES:
    "Connect at least one data source in Pulse or Daton before continuing.",
  SOURCES_CONNECT_WAREHOUSE_FIRST: "Connect your warehouse first",
  GATE_DENIED: "You need company integration access to run setup.",

  COMPLETE_ONBOARDING: "Complete onboarding",
  SETUP_BANNER_TITLE: "Complete your workspace setup",
  SETUP_PROGRESS_SUBTITLE: (completed: number, total: number) =>
    `${completed} of ${total} steps complete. Connect your data to start asking questions.`,
  SETUP_BANNER_SUBTITLE:
    "Finish setup so we can prepare your data. We'll let you know through email or Slack when iQ is ready.",
  COMPOSER_PLACEHOLDER:
    "Complete onboarding to ask iQ about your business",
  SUGGESTIONS_SUBTITLE_GATED:
    "Ask about sales, customers, or marketing performance — once your data is connected.",
} as const;
